"use client";

import { useEffect, useRef } from "react";
import { FriendlyCaptchaSDK } from "@friendlycaptcha/sdk";
const SITEKEY = process.env.NEXT_PUBLIC_FRIENDLYCAPTCHA_SITEKEY;

// One SDK instance shared across all widgets on the page.
let sdk: FriendlyCaptchaSDK | null = null;
interface FriendlyCaptchaWidgetProps {
  /** Called with the response token when solved, or null when reset/expired/errored. */
  onVerify: (token: string | null) => void;
  theme?: "light" | "dark" | "auto";
}

/**
 * Visible Friendly Captcha widget. Starts solving as soon as it mounts and
 * reports the response token via `onVerify`. The token is one-time — it clears
 * on expiry/reset so the parent re-verifies.
 *
 * Mount it only once the visitor has focused the form: the widget's agent
 * iframe keeps the main thread busy (~250 style recalcs and ~130 compositor
 * commits per second, measured) for as long as it exists, even idle.
 */
export function FriendlyCaptchaWidget({
  onVerify,
  theme = "auto"
}: FriendlyCaptchaWidgetProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  // Keep the callback fresh without recreating the widget on every render.
  const onVerifyRef = useRef(onVerify);
  useEffect(() => {
    onVerifyRef.current = onVerify;
  });
  useEffect(() => {
    if (!SITEKEY || !elementRef.current) return;
    sdk ??= new FriendlyCaptchaSDK();
    const widget = sdk.createWidget({
      element: elementRef.current,
      sitekey: SITEKEY,
      startMode: "auto",
      theme
    });
    widget.addEventListener("frc:widget.complete", e => onVerifyRef.current(e.detail.response));
    widget.addEventListener("frc:widget.expire", () => onVerifyRef.current(null));
    widget.addEventListener("frc:widget.error", () => onVerifyRef.current(null));
    return () => widget.destroy();
  }, [theme]);
  return <div ref={elementRef} />;
}